import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  HardwareManagementHerstellerStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_HERSTELLER
} from '../../../hardware-management.shared';
import {map} from 'rxjs/operators';
import {UntypedFormControl, UntypedFormGroup, FormGroupDirective, ReactiveFormsModule} from '@angular/forms';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators,
} from '@softline/ui-core';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {FocusMonitor} from '@angular/cdk/a11y';
import {Store} from '@softline/core';
import {showRequestErrors} from '@softline/application';
import {Hersteller} from '../../../types/hersteller';
import {CommonModule} from '@angular/common';
import {InputBaseComponent} from '../../input-base/input-base.component';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-hersteller-input',
    imports: [CommonModule, InputBaseComponent, ReactiveFormsModule,
    InputComponent,
    TranslatePipe],
    templateUrl: './hersteller-input.component.html',
    styleUrls: ['./hersteller-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HerstellerInputComponent {

  @Input() readonly = false;
  @Input() required = false;

  readonly herstellerInEditing$ = new BehaviorSubject<Hersteller | null>(null);

  readonly entities$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_HERSTELLER,
    HardwareManagementHerstellerStore.getters.all
  ).pipe(map(o => o as Hersteller[]));

  get formGroup(): UntypedFormGroup {
    return this.parentFormGroup.form;
  }

  readonly herstellerForm = new UntypedFormGroup({
    hersteller: new UntypedFormControl(null, Validators.required()),
    gln: new UntypedFormControl(null),
    bezeichnung: new UntypedFormControl(null),
    beschreibung: new UntypedFormControl(null)
  });

  constructor(
    private readonly parentFormGroup: FormGroupDirective,
    private readonly focusMonitor: FocusMonitor,
    private store: Store
  ) { }

  select(hersteller: Hersteller | null | undefined): void {
    if(!hersteller)
      return;
    this.formGroup.patchValue({ hersteller }, { emitEvent: true });

    if (this.formGroup.value?.produkt && this.formGroup.value?.produkt?.hersteller?.id !== hersteller?.id) {
      setTimeout(() => this.formGroup.patchValue({ produkt: null }), 100);
    }
  }

  edit(entity: Hersteller | null | undefined): void {
    if(!entity)
      return;
    this.herstellerInEditing$.next(entity);
    this.herstellerForm.patchValue(entity);
  }

  resetForm(typ?: Hersteller | null): void {
    if (!typ) {
      this.herstellerForm.reset();
      return;
    }

    this.herstellerForm.reset(typ);
  }

  async saveEdit(entity: Hersteller): Promise<void> {
    const updatedEntity = {
      ...entity,
      ...this.herstellerForm.value
    };

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_HERSTELLER,
        HardwareManagementHerstellerStore.actions.update,
        { entity: updatedEntity }
      );

      this.select(result);
      this.herstellerForm.reset();
      this.herstellerInEditing$.next(null);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Hersteller wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  valueChanged(newValue: Hersteller | null | undefined): void {
    if (!newValue) {
      this.herstellerForm.reset();
      this.herstellerInEditing$.next(null);
    }

    if (newValue && this.formGroup.value?.hersteller?.id !== newValue?.id) {
      this.formGroup.patchValue({ hersteller: newValue });

      if (this.formGroup.value?.produkt && this.formGroup.value?.produkt?.hersteller?.id !== newValue?.id) {
        this.formGroup.patchValue({ produkt: null });
      }
    }
  }

  canceled(entityInput?: EntityInputComponent<any>): void {
    this.formGroup.patchValue({ hersteller: null, produkt: null });
    this.herstellerForm.reset();
    this.herstellerInEditing$.next(null);

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.herstellerForm.reset();
  }

  async create(entity: Hersteller): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_HERSTELLER,
        HardwareManagementHerstellerStore.actions.update,
        { entity }
      );

      this.select(result);
      this.herstellerForm.reset();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Hersteller wurde erfolgreich angelegt'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  // We have to use an arrow-function in order to preserve `this` context
  readonly filterTypes = (types: Hersteller[], filter: string): Hersteller[] => {
    if (!filter || filter?.length < 1) {
      return types;
    }

    return types.filter(typ => {
      return typ.hersteller.toLowerCase().includes(filter)
        || typ.bezeichnung.toLowerCase().includes(filter)
        || typ.beschreibung.toLowerCase().includes(filter);
    });
  }
}
